/**
 * TrainingsPage
 *
 * Provider Trainings section: current/past trainings, certifications, required hours remaining.
 * PHI-safe: operational training/cert data only; no patient data.
 *
 * CE period: annual (rolling 12 months). Provider sees own data.
 */

import { useCallback, useState } from 'react';
import {
  Container,
  Group,
  Pagination,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { IconCertificate, IconSchool, IconSettings } from '@tabler/icons-react';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';
import {
  CertificationsTable,
  HoursRemainingSection,
  ManageTrainingsTab,
  TrainingsEmptyState,
  TrainingsFilters,
  TrainingsTable,
} from './components';
import { mockCertifications, mockHoursSummary, mockTrainings } from './data/mockTrainingsData';
import { useTrainingsFiltering } from './hooks/useTrainingsFiltering';
import type { TrainingsFilters as TrainingsFiltersType } from './types';
import { defaultTrainingsFilters } from './types';

const PAGE_SIZE = 10;

export function TrainingsPage(): JSX.Element {
  const [filters, setFilters] = useState<TrainingsFiltersType>(defaultTrainingsFilters);
  const [trainingsPage, setTrainingsPage] = useState(1);

  const { filteredTrainings, hasActiveFilters } = useTrainingsFiltering(mockTrainings, filters);

  const handleClearFilters = useCallback(() => {
    setFilters(defaultTrainingsFilters);
    setTrainingsPage(1);
  }, []);

  const showTrainingsEmpty = filteredTrainings.length === 0;
  const emptyVariant = hasActiveFilters ? 'no-results' : 'no-trainings';

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Group gap="xs" align="center">
              <Title order={2}>Provider Trainings</Title>
              <LocalhostOnlyBadge />
            </Group>
            <Text c="dimmed" size="sm">
              Training completion, certifications, and CE hours
            </Text>
          </div>
        </Group>

        <HoursRemainingSection summary={mockHoursSummary} variant="compliance-tracker" />

        <Tabs defaultValue="trainings" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="trainings" leftSection={<IconSchool size={16} />}>
              Current & past trainings
            </Tabs.Tab>
            <Tabs.Tab value="certifications" leftSection={<IconCertificate size={16} />}>
              Certifications
            </Tabs.Tab>
            <Tabs.Tab value="manage" leftSection={<IconSettings size={16} />}>
              Manage
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="trainings" pt="md">
            <Stack gap="md">
              <TrainingsFilters filters={filters} onChange={setFilters} />
              {showTrainingsEmpty ? (
                <Paper withBorder p="md">
                  <TrainingsEmptyState variant={emptyVariant} onClearFilters={handleClearFilters} />
                </Paper>
              ) : (
                <>
                  <Paper withBorder p="md">
                    <TrainingsTable
                      trainings={filteredTrainings}
                      page={trainingsPage}
                      pageSize={PAGE_SIZE}
                      onPageChange={setTrainingsPage}
                    />
                  </Paper>
                  {filteredTrainings.length > PAGE_SIZE && (
                    <Group justify="center">
                      <Pagination
                        total={Math.ceil(filteredTrainings.length / PAGE_SIZE)}
                        value={trainingsPage}
                        onChange={setTrainingsPage}
                      />
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="certifications" pt="md">
            <Paper withBorder p="md">
              {mockCertifications.length === 0 ? (
                <TrainingsEmptyState variant="no-trainings" />
              ) : (
                <CertificationsTable certifications={mockCertifications} />
              )}
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="manage" pt="md" keepMounted>
            <ManageTrainingsTab />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
